import cx from "classnames";
import type { GatsbyLinkProps } from "gatsby";
import { Link as GatsbyLink } from "gatsby";
import useLocale from "@/hooks/useLocale";
import React from "react";
import { useIsClient } from "@/hooks/useIsClient";
import { hasLocale, resolveLinkLocale } from "utilities/locale";
import { useShellStore } from "@/features/shell/stores/shellStore";

import * as styles from "./Link.module.scss";

export type LinkProps = GatsbyLinkProps<any> & {
  nolang?: boolean;
  to: string;
  noStyles?: boolean;
  stopPropagation?: boolean;
};
export const Link = React.forwardRef<LinkProps, LinkProps>(
  (
    {
      to,
      noStyles = false,
      className,
      children,
      nolang = false,
      onClick,
      stopPropagation = false,
      ...rest
    },
    ref,
  ) => {
    const setModal = useShellStore((state) => state.setModal);
    const external = to && !to.startsWith("/");
    const currentLocale = useLocale();
    const isClient = useIsClient();

    if (external) {
      return (
        <a
          href={to}
          className={className}
          target={to.startsWith("tel:") ? undefined : `_blank`}
          {...rest}
          ref={ref as any}
        >
          {children}
        </a>
      );
    }

    if (isClient && !nolang && !hasLocale(to, currentLocale)) {
      const targetLocale = resolveLinkLocale(to, currentLocale);
      const prefix =
        targetLocale !== window.___defaultLocale ? `/${targetLocale}` : "";
      to = `${prefix}${to}`;
    }

    return (
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      <GatsbyLink
        // gatsby link does not support proper types for refs
        // https://github.com/gatsbyjs/gatsby/pull/37543
        ref={ref as any}
        className={cx(
          {
            [styles.link]: !noStyles,
          },
          className,
        )}
        to={to}
        onClick={(e) => {
          if (stopPropagation) {
            e.stopPropagation();
          }

          setModal(null);
          useShellStore.getState().closeOverlays();

          if (typeof onClick === "function") {
            onClick(e);
          }
        }}
        {...rest}
      >
        {children}
      </GatsbyLink>
    );
  },
);

export default Link;
